import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ExternalLink, TrendingUp, Zap } from "lucide-react";
import { api } from "../../lib/api";
import { statusLabel, statusTone, fmtKwh } from "../../lib/format";

/**
 * SubSitesPanel — renders the direct children of a project alongside a
 * roll-up KPI strip (parent + all descendants combined). Only mounts
 * when the project has at least one child; leaf sites see nothing.
 *
 * Commercial parks often have building-level PV systems + an optional
 * shared canopy on the parent. This panel is the "site" landing view
 * that surfaces each unit and the aggregated position in one glance.
 */
export default function SubSitesPanel({ projectId, project }) {
  const [children, setChildren] = useState([]);
  const [aggregated, setAggregated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const yr = new Date().getUTCFullYear();
    (async () => {
      try {
        const [c, agg] = await Promise.all([
          api.get(`/projects/${projectId}/children`),
          api.get(`/monitoring/${projectId}/monthly-summary?year=${yr}&include_children=1`),
        ]);
        if (cancelled) return;
        setChildren(c.data || []);
        setAggregated(agg.data);
      } catch { /* leaf sites; ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading || !children.length) return null;
  const totalGen = aggregated?.months?.reduce((s, m) => s + (m.generation_kwh || 0), 0) || 0;
  const totalActual = aggregated?.months?.reduce((s, m) => s + (m.actual_kwh || 0), 0) || 0;
  const totalREGOs = aggregated?.months?.at(-1)?.cumulative_regos || 0;

  return (
    <section className="mb-8 border border-[var(--line)] bg-[var(--surface-2)]"
             data-testid="sub-sites-panel">
      <div className="p-4 border-b border-[var(--line)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-[var(--accent)]" />
          <div className="label">Sub-sites · {children.length}</div>
        </div>
        <div className="text-xs muted">
          Roll-up · <span className="font-mono">{project.name}</span> + {children.length} unit{children.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Aggregated KPI strip — parent + descendants combined, current year */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-b border-[var(--line)]">
        <RollupKpi label="Total generation" value={fmtKwh(totalGen)} icon={<Zap size={14} />} testid="sub-sites-total-gen" />
        <RollupKpi label="Total actual" value={fmtKwh(totalActual)} icon={<TrendingUp size={14} />} testid="sub-sites-total-actual" />
        <RollupKpi label="REGOs earned" value={totalREGOs.toLocaleString()} testid="sub-sites-total-regos" />
      </div>

      {/* Per-child list */}
      <div className="divide-y divide-[var(--line)]">
        {children.map((c) => (
          <Link key={c.id} to={`/projects/${c.id}`}
                className="flex items-center gap-3 p-4 hover:bg-white transition-colors"
                data-testid={`sub-site-row-${c.id}`}>
            <Building2 size={14} className="text-zinc-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.name}</div>
              <div className="text-xs muted truncate">{c.address || c.customer_name}</div>
            </div>
            <span className={`tag ${statusTone(c.status)}`}>{statusLabel(c.status)}</span>
            <ExternalLink size={12} className="text-zinc-400" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function RollupKpi({ label, value, icon, testid }) {
  return (
    <div className="p-4 border-r border-[var(--line)] last:border-r-0" data-testid={testid}>
      <div className="label flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
